import React, { useState, useEffect } from 'react';
import Input from '../common/Input';
import Button from '../common/Button';
import Badge from '../common/Badge';
import { customersAPI } from '../../api/customers';
import { formatCurrency } from '../../utils/formatters';

const PurchaseSubscriptionForm = ({ plans, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    customerId: '',
    subscriptionPlanId: '',
    paymentMethod: 'cash',
    receivedAmount: '',
    notes: ''
  });
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchCustomers();
  }, [searchTerm]);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const response = await customersAPI.getAll({ 
        search: searchTerm,
        isActive: true,
        limit: 50
      });
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    if (name === 'subscriptionPlanId') {
      const plan = plans.find(p => p._id === value);
      setSelectedPlan(plan);
      // Auto-fill received amount with plan price
      if (plan && formData.paymentMethod === 'cash') {
        setFormData(prev => ({ ...prev, receivedAmount: plan.price.toString() }));
      }
    }

    if (name === 'customerId') {
      const customer = customers.find(c => c._id === value);
      setSelectedCustomer(customer);
    }

    // Auto-fill received amount when payment method changes
    if (name === 'paymentMethod' && selectedPlan) {
      if (value === 'cash') {
        setFormData(prev => ({ ...prev, receivedAmount: selectedPlan.price.toString() }));
      } else {
        setFormData(prev => ({ ...prev, receivedAmount: selectedPlan.price.toString() }));
      }
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.customerId) {
      newErrors.customerId = 'Please select a customer';
    }

    if (!formData.subscriptionPlanId) {
      newErrors.subscriptionPlanId = 'Please select a subscription plan';
    }

    if (!formData.paymentMethod) {
      newErrors.paymentMethod = 'Please select a payment method';
    }

    // Validate received amount for cash/card
    if (formData.paymentMethod === 'cash' || formData.paymentMethod === 'card') {
      if (!formData.receivedAmount || parseFloat(formData.receivedAmount) <= 0) {
        newErrors.receivedAmount = 'Please enter received amount';
      } else if (parseFloat(formData.receivedAmount) < selectedPlan.price) {
        newErrors.receivedAmount = `Amount must be at least ${formatCurrency(selectedPlan.price)}`;
      }
    }

    // Validate wallet balance
    if (formData.paymentMethod === 'wallet') {
      if (!selectedCustomer || (selectedCustomer.walletBalance || 0) < selectedPlan.price) {
        newErrors.paymentMethod = 'Insufficient wallet balance';
      }
    }

    // Validate credit limit
    if (formData.paymentMethod === 'on_credit') {
      const availableCredit = (selectedCustomer?.creditLimit || 0) - (selectedCustomer?.creditUsed || 0);
      if (availableCredit < selectedPlan.price) {
        newErrors.paymentMethod = 'Insufficient credit limit';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validate()) {
      const submitData = {
        customerId: formData.customerId,
        subscriptionPlanId: formData.subscriptionPlanId,
        paymentMethod: formData.paymentMethod,
        notes: formData.notes,
      };

      // Add payment details
      if (formData.paymentMethod === 'cash' || formData.paymentMethod === 'card') {
        submitData.receivedAmount = parseFloat(formData.receivedAmount);
      }

      onSubmit(submitData);
    }
  };

  const changeAmount = selectedPlan && formData.receivedAmount
    ? Math.max(0, parseFloat(formData.receivedAmount) - selectedPlan.price)
    : 0;

  return (
    <form onSubmit={handleSubmit} className="form">
      {/* Search Customer */}
      <div className="form-row">
        <div className="input-group">
          <label className="input-label">Search Customer</label>
          <input
            type="text"
            className="input"
            placeholder="Search by name or phone (8 digits)..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {loading && <span className="input-hint">Searching...</span>}
        </div>
      </div>

      {/* Select Customer */}
      <div className="form-row">
        <div className="input-group">
          <label className="input-label">
            Select Customer <span className="text-danger">*</span>
          </label>
          <select
            name="customerId"
            className={`input ${errors.customerId ? 'input-error' : ''}`}
            value={formData.customerId}
            onChange={handleChange}
            required
          >
            <option value="">-- Select Customer --</option>
            {customers.map((customer) => (
              <option key={customer._id} value={customer._id}>
                {customer.name} - {customer.phone}
                {customer.walletBalance > 0 && ` (Wallet: ${formatCurrency(customer.walletBalance)})`}
              </option>
            ))}
          </select>
          {errors.customerId && (
            <span className="error-message">{errors.customerId}</span>
          )}
          {customers.length === 0 && !loading && searchTerm && (
            <span className="input-hint">No customers found. Try a different search.</span>
          )}
        </div>
      </div>

      {/* Customer Financial Info */}
      {selectedCustomer && (
        <div style={{
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: 8,
          padding: 12,
          marginBottom: 16,
          display: 'flex',
          gap: 16,
          flexWrap: 'wrap'
        }}>
          {selectedCustomer.walletBalance > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 16 }}>👛</span>
              <span style={{ fontSize: 12, color: '#64748b' }}>Wallet:</span>
              <strong style={{ color: '#10b981' }}>{formatCurrency(selectedCustomer.walletBalance)}</strong>
            </div>
          )}
          {selectedCustomer.creditLimit > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 16 }}>📝</span>
              <span style={{ fontSize: 12, color: '#64748b' }}>Available Credit:</span>
              <strong style={{ color: '#f59e0b' }}>
                {formatCurrency((selectedCustomer.creditLimit || 0) - (selectedCustomer.creditUsed || 0))}
              </strong>
            </div>
          )}
        </div>
      )}

      {/* Select Plan */}
      <div className="form-row">
        <div className="input-group">
          <label className="input-label">
            Subscription Plan <span className="text-danger">*</span>
          </label>
          <select
            name="subscriptionPlanId"
            className={`input ${errors.subscriptionPlanId ? 'input-error' : ''}`}
            value={formData.subscriptionPlanId}
            onChange={handleChange}
            required
          >
            <option value="">-- Select Plan --</option>
            {plans.map((plan) => (
              <option key={plan._id} value={plan._id}>
                {plan.name} - {formatCurrency(plan.price)} ({plan.duration} days)
              </option>
            ))}
          </select>
          {errors.subscriptionPlanId && (
            <span className="error-message">{errors.subscriptionPlanId}</span>
          )}
        </div>
      </div>

      {/* Plan Details Preview */}
      {selectedPlan && (
        <div className="plan-preview kuwait-plan-preview">
          <div className="plan-preview-header">
            <h4>📋 Plan Details</h4>
            <Badge variant="success" size="small">Selected</Badge>
          </div>
          
          <div className="plan-preview-content">
            <div className="preview-row">
              <span className="preview-label">💰 Price:</span>
              <span className="preview-value kuwait-price">{formatCurrency(selectedPlan.price)}</span>
            </div>
            <div className="preview-row">
              <span className="preview-label">📅 Duration:</span>
              <span className="preview-value">{selectedPlan.duration} days</span>
            </div>
            
            {selectedPlan.benefits.discountPercentage > 0 && (
              <div className="preview-row highlight">
                <span className="preview-label">💸 Discount:</span>
                <span className="preview-value discount">{selectedPlan.benefits.discountPercentage}% OFF all orders</span>
              </div>
            )}
            
            {selectedPlan.benefits.bundleClothes > 0 && (
              <div className="preview-row">
                <span className="preview-label">👔 Bundle:</span>
                <span className="preview-value">{selectedPlan.benefits.bundleClothes} items included</span>
              </div>
            )}
            
            {selectedPlan.benefits.freeItems > 0 && (
              <div className="preview-row">
                <span className="preview-label">🎁 Free Items:</span>
                <span className="preview-value">{selectedPlan.benefits.freeItems} per month</span>
              </div>
            )}
            
            {selectedPlan.benefits.expressDeliveryFree && (
              <div className="preview-row highlight">
                <span className="preview-label">🚀 Express Delivery:</span>
                <Badge variant="success" size="small">FREE</Badge>
              </div>
            )}
            
            {selectedPlan.benefits.priorityService && (
              <div className="preview-row">
                <span className="preview-label">⭐ Priority:</span>
                <Badge variant="primary" size="small">YES</Badge>
              </div>
            )}
          </div>

          {/* Value calculation */}
          {selectedPlan.benefits.discountPercentage > 0 && (
            <div className="plan-savings">
              <div className="savings-title">💡 Estimated Savings:</div>
              <div className="savings-text">
                If customer spends KD 100.000/month, they save{' '}
                <strong>KD {(100 * selectedPlan.benefits.discountPercentage / 100).toFixed(3)}</strong>{' '}
                = Plan pays for itself!
              </div>
            </div>
          )}
        </div>
      )}

      {/* Payment Section */}
      {selectedPlan && (
        <>
          <div style={{
            background: '#fffbeb',
            border: '1.5px solid #fde68a',
            borderRadius: 10,
            padding: 16,
            marginTop: 16
          }}>
            <h4 style={{ fontSize: 14, fontWeight: 700, color: '#92400e', marginBottom: 12 }}>
              💳 Payment Collection
            </h4>

            {/* Payment Method */}
            <div className="form-row">
              <div className="input-group">
                <label className="input-label">
                  Payment Method <span className="text-danger">*</span>
                </label>
                <select
                  name="paymentMethod"
                  className={`input ${errors.paymentMethod ? 'input-error' : ''}`}
                  value={formData.paymentMethod}
                  onChange={handleChange}
                  required
                >
                  <option value="cash">💵 Cash</option>
                  <option value="card">💳 Card</option>
                  {selectedCustomer?.walletBalance >= selectedPlan.price && (
                    <option value="wallet">👛 Wallet (Available: {formatCurrency(selectedCustomer.walletBalance)})</option>
                  )}
                  {selectedCustomer?.creditLimit > 0 && 
                   ((selectedCustomer.creditLimit - (selectedCustomer.creditUsed || 0)) >= selectedPlan.price) && (
                    <option value="on_credit">
                      📝 Credit (Available: {formatCurrency(selectedCustomer.creditLimit - (selectedCustomer.creditUsed || 0))})
                    </option>
                  )}
                </select>
                {errors.paymentMethod && (
                  <span className="error-message">{errors.paymentMethod}</span>
                )}
              </div>
            </div>

            {/* Received Amount (for cash/card only) */}
            {(formData.paymentMethod === 'cash' || formData.paymentMethod === 'card') && (
              <div className="form-row">
                <Input
                  label={`Amount Received (${formData.paymentMethod === 'cash' ? 'Cash' : 'Card'})`}
                  name="receivedAmount"
                  type="number"
                  step="0.001"
                  value={formData.receivedAmount}
                  onChange={handleChange}
                  error={errors.receivedAmount}
                  placeholder={selectedPlan.price.toFixed(3)}
                  required
                />
              </div>
            )}

            {/* Change Amount (for cash only) */}
            {formData.paymentMethod === 'cash' && formData.receivedAmount && changeAmount > 0 && (
              <div style={{
                background: '#d1fae5',
                border: '1px solid #86efac',
                borderRadius: 8,
                padding: 12,
                marginTop: 8,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span style={{ fontSize: 13, color: '#065f46', fontWeight: 600 }}>💵 Change to Return:</span>
                <strong style={{ fontSize: 18, color: '#065f46' }}>{formatCurrency(changeAmount)}</strong>
              </div>
            )}

            {/* Payment Summary */}
            <div style={{
              background: '#fff',
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              padding: 12,
              marginTop: 12
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 13, color: '#64748b' }}>Plan Price:</span>
                <strong>{formatCurrency(selectedPlan.price)}</strong>
              </div>
              {formData.paymentMethod === 'wallet' && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontSize: 13, color: '#64748b' }}>Pay from Wallet:</span>
                  <strong style={{ color: '#8b5cf6' }}>{formatCurrency(selectedPlan.price)}</strong>
                </div>
              )}
              {formData.paymentMethod === 'on_credit' && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontSize: 13, color: '#64748b' }}>Pay on Credit:</span>
                  <strong style={{ color: '#f59e0b' }}>{formatCurrency(selectedPlan.price)}</strong>
                </div>
              )}
              <div style={{
                borderTop: '2px solid #e2e8f0',
                paddingTop: 8,
                marginTop: 8,
                display: 'flex',
                justifyContent: 'space-between'
              }}>
                <span style={{ fontSize: 14, fontWeight: 700 }}>Total to Pay:</span>
                <strong style={{ fontSize: 16, color: '#1d4ed8' }}>{formatCurrency(selectedPlan.price)}</strong>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Notes */}
      <div className="form-row">
        <div className="input-group">
          <label className="input-label">Notes (Optional)</label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            className="input"
            rows="3"
            placeholder="Add any special notes about this subscription purchase..."
          />
        </div>
      </div>

      {/* Form Actions */}
      <div className="form-actions">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" variant="primary">
          🛒 Purchase & Collect Payment
        </Button>
      </div>
    </form>
  );
};

export default PurchaseSubscriptionForm;
